/**
 * Leviathan Web - Order Form Page
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Create and edit orders with line items, customer selection, and pricing
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  readApiFailure,
  focusFirstFieldError,
  humanizeFieldPath,
  type FieldErrors,
} from '@/utils/formErrors';
import { FieldError, fieldBorder } from '@components/ui/FieldError';

import { API_URL } from '@/config/api';

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  taxExempt: boolean;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  /**
   * Genuinely nullable. A product priced per contract has no list price, and
   * until this session the product form did not collect one at all, so every
   * product created through the UI had `null` here.
   */
  pricePerUnit: number | null;
  unit: string;
}

interface CustomerLocation {
  id: string;
  name: string;
  locationCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  instructions: string | null;
}

interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  unit: string;
  /**
   * `null` means "the operator has not given this line a price yet" — which is
   * what happens when the chosen product has no list price. It is NOT zero:
   * posting it as zero would create a real order for no money and look
   * deliberate. Submission refuses it, at the field.
   */
  unitPrice: number | null;
  lineTotal: number;
}

interface OrderFormData {
  customerId: string;
  customerLocationId: string;
  orderType: string;
  requestedDate: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  deliveryInstructions: string;
  poNumber: string;
  taxRate: number;
  deliveryFee: number;
  notes: string;
  items: OrderItem[];
}

export default function OrderFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Per-field messages, keyed by the same path the API reports
   * (`customerId`, `items.0.productId`, ...). The banner says how many; these
   * say which, next to the input that has to change.
   */
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const clearFieldError = (path: string) =>
    setFieldErrors((prev) => {
      if (prev[path] === undefined) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [locations, setLocations] = useState<CustomerLocation[]>([]);

  // Get query params for pre-selection
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedCustomerId = urlParams.get('customerId') || '';
  const preselectedLocationId = urlParams.get('locationId') || '';

  const [formData, setFormData] = useState<OrderFormData>({
    customerId: preselectedCustomerId,
    customerLocationId: preselectedLocationId,
    orderType: 'DELIVERY',
    requestedDate: new Date().toISOString().split('T')[0] || '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZip: '',
    deliveryInstructions: '',
    poNumber: '',
    taxRate: 0,
    deliveryFee: 0,
    notes: '',
    items: [],
  });

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    if (isEditMode) {
      fetchOrder();
    } else if (preselectedCustomerId) {
      fetchLocationsForCustomer(preselectedCustomerId);
      // Pre-fill address from location if provided
      if (preselectedLocationId) {
        fetchAndApplyLocation(preselectedCustomerId, preselectedLocationId);
      }
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers?isActive=true&limit=500`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?isActive=true&limit=500`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.items || data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchLocationsForCustomer = async (customerId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/customer-locations?customerId=${customerId}&isActive=true`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchAndApplyLocation = async (_customerId: string, locationId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/customer-locations/${locationId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const location = data.location;
        if (location) {
          setFormData((prev) => ({
            ...prev,
            deliveryAddress: location.address || '',
            deliveryCity: location.city || '',
            deliveryState: location.state || '',
            deliveryZip: location.zip || '',
            deliveryInstructions: location.instructions || prev.deliveryInstructions,
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch location:', error);
    }
  };

  const fetchOrder = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const order = data.order;

        // Fetch locations if order has a customer
        if (order.customerId) {
          await fetchLocationsForCustomer(order.customerId);
        }

        setFormData({
          customerId: order.customerId,
          customerLocationId: order.customerLocationId || '',
          orderType: order.orderType,
          requestedDate: order.requestedDate?.split('T')[0] || '',
          deliveryAddress: order.deliveryAddress || '',
          deliveryCity: order.deliveryCity || '',
          deliveryState: order.deliveryState || '',
          deliveryZip: order.deliveryZip || '',
          deliveryInstructions: order.deliveryInstructions || '',
          poNumber: order.poNumber || '',
          taxRate: order.taxRate || 0,
          deliveryFee: order.deliveryFee || 0,
          notes: order.notes || '',
          items: order.items?.map((item: any) => ({
            productId: item.productId,
            productName: item.product?.name || '',
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
          })) || [],
        });
      } else {
        setError('Order not found');
      }
    } catch (error) {
      setError('Failed to load order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find((c) => c.id === customerId);

    if (customer) {
      setFormData((prev) => ({
        ...prev,
        customerId,
        customerLocationId: '', // Reset location when customer changes
        deliveryAddress: customer.deliveryAddress || '',
        deliveryCity: customer.deliveryCity || '',
        deliveryState: customer.deliveryState || '',
        deliveryZip: customer.deliveryZip || '',
        taxRate: customer.taxExempt ? 0 : prev.taxRate,
      }));
      fetchLocationsForCustomer(customerId);
    } else {
      setFormData((prev) => ({ ...prev, customerId, customerLocationId: '' }));
      setLocations([]);
    }
  };

  const handleLocationChange = (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);

    setFormData((prev) => ({
      ...prev,
      customerLocationId: locationId,
      ...(location
        ? {
            deliveryAddress: location.address || prev.deliveryAddress,
            deliveryCity: location.city || prev.deliveryCity,
            deliveryState: location.state || prev.deliveryState,
            deliveryZip: location.zip || prev.deliveryZip,
            deliveryInstructions: location.instructions || prev.deliveryInstructions,
          }
        : {}),
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: '',
          productName: '',
          quantity: 0,
          unit: 'gallon',
          unitPrice: null,
          lineTotal: 0,
        },
      ],
    }));
  };

  const updateItem = (index: number, field: keyof OrderItem, value: string | number | null) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      if (!currentItem) return prev;

      const item: OrderItem = {
        productId: currentItem.productId,
        productName: currentItem.productName,
        quantity: currentItem.quantity,
        unit: currentItem.unit,
        unitPrice: currentItem.unitPrice,
        lineTotal: currentItem.lineTotal,
        [field]: value,
      };

      // If product changed, update price and name
      if (field === 'productId') {
        const product = products.find((p) => p.id === value);
        if (product) {
          item.productName = product.name;
          item.unitPrice = product.pricePerUnit;
          item.unit = product.unit;
        }
      }

      // Recalculate line total. A missing price shows as $0.00 on screen; it
      // is `null` in the data, and submission refuses it rather than sending
      // a zero-priced line.
      item.lineTotal = (item.quantity || 0) * (item.unitPrice ?? 0);
      newItems[index] = item;

      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxAmount = subtotal * (formData.taxRate / 100);
    const total = subtotal + taxAmount + formData.deliveryFee;
    return { subtotal, taxAmount, total };
  };

  /**
   * Everything this form can decide for itself, reported the same way the API
   * reports it: keyed by field path, so one rendering path covers both.
   */
  const validateLocally = (): FieldErrors => {
    const issues: FieldErrors = {};

    if (!formData.customerId) {
      issues.customerId = 'Select a customer';
    }

    if (formData.items.length === 0) {
      issues.items = 'Add at least one item before creating the order';
    }

    formData.items.forEach((item, index) => {
      if (!item.productId) {
        issues[`items.${index}.productId`] = 'Select a product';
      }
      if (!(item.quantity > 0)) {
        issues[`items.${index}.quantity`] = 'Enter a quantity greater than zero';
      }
      if (item.unitPrice == null) {
        // Reached whenever the product carries no list price. Caught here, at
        // the field, rather than as a 422 from the API two seconds later.
        issues[`items.${index}.unitPrice`] =
          'This product has no set price — enter the price for this line';
      }
    });

    return issues;
  };

  const reportIssues = (issues: FieldErrors, fallback: string) => {
    setFieldErrors(issues);
    const paths = Object.keys(issues);
    if (paths.length === 0) {
      setError(fallback);
      return;
    }
    const first = paths[0]!;
    setError(
      paths.length === 1
        ? `${humanizeFieldPath(first)}: ${issues[first]}`
        : `${paths.length} fields need attention — they are marked below. First: ${humanizeFieldPath(first)}.`
    );
    focusFirstFieldError(issues);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const localIssues = validateLocally();
    if (Object.keys(localIssues).length > 0) {
      reportIssues(localIssues, 'Please complete the order before saving');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/orders/${id}` : `${API_URL}/api/orders`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          /**
           * "Use custom address below" is an empty option value, and an empty
           * string is not a location id. Send null, which is what the operator
           * means and what clears the field on an edit.
           */
          customerLocationId: formData.customerLocationId || null,
          items: formData.items.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
          })),
        }),
      });

      if (response.ok) {
        navigate('/orders');
      } else {
        /**
         * `readApiFailure` ALWAYS returns a string message.
         *
         * This line used to be `setError(data.message || data.error || ...)`.
         * The API's failure body has no top-level `message`, so that stored the
         * `error` OBJECT in state, `{error}` rendered an object as a React
         * child, React threw, and the whole application unmounted to a white
         * page. That is the blank screen reported from the demo instance.
         */
        const failure = await readApiFailure(response, 'Failed to save order');
        setError(failure.message);
        setFieldErrors(failure.fieldErrors);
        focusFirstFieldError(failure.fieldErrors);
      }
    } catch (error) {
      setError(
        error instanceof Error && error.message
          ? `Failed to save order: ${error.message}`
          : 'Failed to save order'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Enter does not place the order.
   *
   * A single Enter keystroke in any text input natively submits the form. On
   * this form that is a purchase: it runs a credit check and creates an order
   * with a number, which then has to be cancelled. The operator who reported
   * this hit Enter after typing a quantity and the order attempt fired while
   * he was still filling the form in.
   *
   * Enter still works everywhere it should — inside a textarea it inserts a
   * newline, and on a focused button it activates that button — because both
   * are excluded here. The order is placed by pressing "Create Order", which
   * is the only control that says it does that.
   */
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;

    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
    if (target instanceof HTMLElement && target.isContentEditable) return;

    e.preventDefault();
    // Commit what was typed, so the totals update as if the operator had
    // tabbed out of the field.
    if (target && typeof (target as HTMLInputElement).blur === 'function') {
      (target as HTMLInputElement).blur();
    }
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading order...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Order' : 'New Order'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify order details' : 'Create a new customer order'}
              </p>
            </div>
            <button
              onClick={() => navigate('/orders')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider transition-colors"
            >
              &larr; Back to Orders
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-6">
        {/* Customer & Order Type */}
        <Panel title="Order Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Customer *
              </label>
              <select
                data-field="customerId"
                value={formData.customerId}
                onChange={(e) => {
                  clearFieldError('customerId');
                  handleCustomerChange(e.target.value);
                }}
                className={`w-full bg-background border ${fieldBorder(
                  Boolean(fieldErrors.customerId)
                )} focus:ring-1 px-4 py-2 text-text-primary font-mono text-sm`}
                required
              >
                <option value="">Select Customer...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.customerNumber})
                  </option>
                ))}
              </select>
              <FieldError error={fieldErrors.customerId} />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Order Type *
              </label>
              <select
                value={formData.orderType}
                onChange={(e) => setFormData((prev) => ({ ...prev, orderType: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                <option value="DELIVERY">Delivery</option>
                <option value="PICKUP">Pickup</option>
                <option value="WILL_CALL">Will Call</option>
                <option value="EMERGENCY">Emergency</option>
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Requested Date
              </label>
              <input
                type="date"
                value={formData.requestedDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, requestedDate: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                PO Number
              </label>
              <input
                type="text"
                value={formData.poNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, poNumber: e.target.value }))}
                placeholder="Customer PO..."
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Ship-To Location */}
        {formData.customerId && formData.orderType === 'DELIVERY' && (
          <Panel title="Ship-To Location">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Select Location
                </label>
                <select
                  value={formData.customerLocationId}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                >
                  <option value="">Use custom address below</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                      {location.locationCode && ` (${location.locationCode})`}
                      {location.city && ` - ${location.city}`}
                      {location.state && `, ${location.state}`}
                    </option>
                  ))}
                </select>
                {locations.length === 0 && (
                  <p className="text-text-tertiary text-xs font-mono mt-1">
                    No saved locations for this customer. Enter address below or create a location first.
                  </p>
                )}
                {formData.customerLocationId && (
                  <p className="text-success-green text-xs font-mono mt-1">
                    Address auto-filled from selected location
                  </p>
                )}
              </div>
            </div>
          </Panel>
        )}

        {/* Delivery Address */}
        {formData.orderType === 'DELIVERY' && (
          <Panel title="Delivery Address">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.deliveryAddress}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, deliveryAddress: e.target.value }))
                  }
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.deliveryCity}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, deliveryCity: e.target.value }))
                  }
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryState}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, deliveryState: e.target.value }))
                    }
                    className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryZip}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, deliveryZip: e.target.value }))
                    }
                    className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Delivery Instructions
                </label>
                <textarea
                  value={formData.deliveryInstructions}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, deliveryInstructions: e.target.value }))
                  }
                  rows={2}
                  placeholder="Gate codes, special instructions..."
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
            </div>
          </Panel>
        )}

        {/* Order Items */}
        <Panel title="Order Items">
          <div className="space-y-4">
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-text-tertiary font-mono">No items added yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Product
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-32">
                        Quantity
                      </th>
                      <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Unit
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-32">
                        Price
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-32">
                        Total
                      </th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="border-b border-panel-border">
                        <td className="px-4 py-2">
                          <select
                            data-field={`items.${index}.productId`}
                            value={item.productId}
                            onChange={(e) => {
                              clearFieldError(`items.${index}.productId`);
                              updateItem(index, 'productId', e.target.value);
                            }}
                            className={`w-full bg-background border ${fieldBorder(
                              Boolean(fieldErrors[`items.${index}.productId`])
                            )} px-2 py-1 text-text-primary font-mono text-sm`}
                          >
                            <option value="">Select Product...</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} ({product.sku})
                              </option>
                            ))}
                          </select>
                          <FieldError error={fieldErrors[`items.${index}.productId`]} />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            data-field={`items.${index}.quantity`}
                            type="number"
                            value={item.quantity || ''}
                            onChange={(e) => {
                              clearFieldError(`items.${index}.quantity`);
                              updateItem(index, 'quantity', parseFloat(e.target.value) || 0);
                            }}
                            min="0"
                            step="1"
                            className={`w-full bg-background border ${fieldBorder(
                              Boolean(fieldErrors[`items.${index}.quantity`])
                            )} px-2 py-1 text-text-primary font-mono text-sm text-right`}
                          />
                          <FieldError error={fieldErrors[`items.${index}.quantity`]} />
                        </td>
                        <td className="px-4 py-2">
                          <span className="text-text-secondary font-mono text-sm">{item.unit}</span>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            data-field={`items.${index}.unitPrice`}
                            type="number"
                            value={item.unitPrice ?? ''}
                            onChange={(e) => {
                              clearFieldError(`items.${index}.unitPrice`);
                              // Empty stays null — "no price yet", not "free".
                              const raw = e.target.value;
                              const parsed = raw.trim() === '' ? null : parseFloat(raw);
                              updateItem(
                                index,
                                'unitPrice',
                                parsed === null || Number.isNaN(parsed) ? null : parsed
                              );
                            }}
                            min="0"
                            step="0.01"
                            className={`w-full bg-background border ${fieldBorder(
                              Boolean(fieldErrors[`items.${index}.unitPrice`])
                            )} px-2 py-1 text-text-primary font-mono text-sm text-right`}
                          />
                          <FieldError error={fieldErrors[`items.${index}.unitPrice`]} />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-amber font-mono font-semibold">
                            {/* Guarded: on the EDIT path lineTotal comes from the
                                API, and `null.toFixed()` is the same class of
                                unhandled render error as the one that blanked
                                this screen. */}
                            ${(item.lineTotal || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-error-red hover:text-error-red/80 font-mono text-sm"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              data-field="items"
              onClick={() => {
                clearFieldError('items');
                addItem();
              }}
              className={`w-full border border-dashed ${
                fieldErrors.items ? 'border-error-red text-error-red' : 'border-panel-border text-text-secondary'
              } hover:border-amber hover:text-amber py-3 font-mono text-sm transition-colors`}
            >
              + Add Item
            </button>
            <FieldError error={fieldErrors.items} />
          </div>
        </Panel>

        {/* Totals & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Panel title="Notes">
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              rows={4}
              placeholder="Order notes..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
            />
          </Panel>

          <Panel title="Order Totals">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary font-mono">Subtotal</span>
                <span className="text-text-primary font-mono">${subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Tax Rate (%)</span>
                  <input
                    type="number"
                    value={formData.taxRate}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))
                    }
                    min="0"
                    step="0.01"
                    className="w-20 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">${taxAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Delivery Fee</span>
                  <input
                    type="number"
                    value={formData.deliveryFee}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        deliveryFee: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">${formData.deliveryFee.toFixed(2)}</span>
              </div>

              <div className="border-t border-panel-border pt-4 flex justify-between items-center">
                <span className="text-text-primary font-mono font-semibold text-lg">TOTAL</span>
                <span
                  className="text-amber font-mono font-bold text-2xl"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.5)' }}
                >
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Order' : 'Create Order'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
